import { useCallback, useEffect, useState } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { formatFileSize } from '@/lib/schemas'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { DocumentVersion, DocumentVersionHistory, LibraryDocument } from '@/lib/schemas'

interface DocumentVersionsDialogProps {
  document: LibraryDocument
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatDate(value: string | null): string | null {
  if (!value) return null
  return new Date(value).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Read-only version history for a library document (F26).
 *
 * Answers "which file was current when we submitted in March, and who changed
 * it?" — the audit question a policy/certification library exists for. Restore
 * is deliberately out of scope: the retained predecessors would support it, but
 * a rollback has to re-index and re-derive the PDF, which is the replace path,
 * not a list affordance.
 */
export function DocumentVersionsDialog({
  document,
  open,
  onOpenChange,
}: DocumentVersionsDialogProps) {
  const { fetchDocumentVersions, getPreviousVersionUrl, getDownloadUrl } = useLibraryStore()

  const [history, setHistory] = useState<DocumentVersionHistory | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false

    setIsLoading(true)
    setError(null)
    fetchDocumentVersions(document.id)
      .then((result) => {
        if (!cancelled) setHistory(result)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load version history')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, document.id, fetchDocumentVersions])

  const handleDownload = useCallback(
    async (entry: DocumentVersion) => {
      setDownloadingVersion(entry.version)
      try {
        const url = entry.isCurrent
          ? await getDownloadUrl(document.id)
          : await getPreviousVersionUrl(document.id, entry.version)
        window.open(url, '_blank')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to download this version')
      } finally {
        setDownloadingVersion(null)
      }
    },
    [document.id, getDownloadUrl, getPreviousVersionUrl]
  )

  const trimmed = history !== null && history.oldestRetainedVersion > 1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{document.name}</span>
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        {!isLoading && !error && history && (
          <div className="divide-y divide-border -mx-1">
            {history.versions.map((entry) => {
              const added = formatDate(entry.addedAt)
              const superseded = formatDate(entry.supersededAt)
              return (
                <div key={entry.version} className="flex items-start gap-3 px-1 py-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" dot={false} className="tabular-nums">
                        v{entry.version}
                      </Badge>
                      {entry.isCurrent && (
                        <span className="text-xs font-medium text-foreground">Current</span>
                      )}
                      <span className="text-meta tabular-nums">
                        {formatFileSize(entry.size)}
                      </span>
                    </div>
                    <p className="text-meta">
                      {added
                        ? `Added ${added}${entry.addedByName ? ` by ${entry.addedByName}` : ''}`
                        : 'Added before the retained history begins'}
                    </p>
                    {superseded && (
                      <p className="text-meta">
                        Superseded {superseded}
                        {entry.supersededByName ? ` by ${entry.supersededByName}` : ''}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Download version ${entry.version}`}
                    disabled={downloadingVersion === entry.version}
                    onClick={() => void handleDownload(entry)}
                  >
                    {downloadingVersion === entry.version ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )
            })}
          </div>
        )}

        {!isLoading && !error && trimmed && (
          <p className="text-meta leading-relaxed">
            {/* The cap is on PREVIOUS versions (5) — the current file is not one
                of them, which is why a v7 document can list 6 rows here. */}
            Only the 5 most recent previous versions are kept —{' '}
            {history.oldestRetainedVersion > 2
              ? `v1–v${history.oldestRetainedVersion - 1} are`
              : 'v1 is'}{' '}
            no longer available.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
